import type { BackendAPITypes } from '../../../generated/blueprint.ts';
import { Money, toMoneyFromCents } from '../../../lib/Money.ts';
import type { FormInstance } from '../../../components/form-library/types.ts';
import { DefaultVatRateOption } from '../types/VatCode.ts';
import type { DocumentRelation } from '../types/Document.ts';
import { lineItemFieldName } from './lineItemsHelper.ts';
import type { CurrencyEnum } from '../../../generated/enums.ts';

export type EditableBillWire = NonNullable<
    BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerClientGetDocumentGetResponseBody['editable_bill']
>;
export type EditableBillLineItemWire = EditableBillWire['line_items'][number];

export type EditorFormValues = Omit<EditableBillWire, 'is_vat_ignored' | 'line_items' | 'relation'> & {
    is_vat_toggled_on: boolean;
    currency: CurrencyEnum;
    client_name: string;
    relation_bookkeeping?: string | null;
};

const DEFAULT_CURRENCY_CODE: CurrencyEnum = 'EUR';

export function relationFromEditableBill(relation: EditableBillWire['relation']): DocumentRelation | null {
    if (!relation?.id) {
        return null;
    }
    return { id: relation.id, name: relation.name ?? '' };
}

export function buildFormValuesFromEditableBill(
    editable: EditableBillWire,
    clientBusinessName: string | null,
): EditorFormValues {
    const currency = editable.total_amount_payable?.currency ?? DEFAULT_CURRENCY_CODE;
    return {
        document_category: editable.document_category ?? null,
        document_direction: editable.document_direction ?? null,
        relation_name: editable.relation_name ?? '',
        relation_iban: editable.relation_iban ?? '',
        relation_country: editable.relation_country ?? null,
        vat_number: editable.vat_number ?? '',
        reference_identifier: editable.reference_identifier ?? '',
        payment_reference: editable.payment_reference ?? '',
        normalized_date: editable.normalized_date ?? '',
        normalized_time: editable.normalized_time ?? '',
        normalized_due_date: editable.normalized_due_date ?? '',
        currency,
        total_amount_payable: (editable.total_amount_payable as Money | null) ?? toMoneyFromCents(0, currency),
        vat_amount_overall: (editable.vat_amount_overall as Money | null) ?? null,
        is_credit_note: editable.is_credit_note,
        vat_reverse_charge_applied: editable.vat_reverse_charge_applied ?? null,
        is_vat_toggled_on: !editable.is_vat_ignored,
        client_name: clientBusinessName ?? '',
        version: editable.version,
        content_type: editable.content_type,
    };
}

export function resolveVatRateOptionId(
    item: Pick<EditableBillLineItemWire, 'vat_rate_percentage' | 'is_vat_exempt'>,
    vatRateOptions: DefaultVatRateOption[],
): string | null {
    if (item.vat_rate_percentage === 0) {
        return item.is_vat_exempt ? 'VAT_NONE' : 'VAT_0';
    }
    return vatRateOptions.find(option => option.percentage === item.vat_rate_percentage)?.value ?? null;
}

export function buildLineItemFormValues(
    lineItems: EditableBillLineItemWire[],
    vatRateOptions: DefaultVatRateOption[],
): { values: Record<string, unknown>; netAmountsSum: number; vatAmountsSum: number } {
    const values: Record<string, unknown> = {};
    let netAmountsSum = 0;
    let vatAmountsSum = 0;

    lineItems.forEach((item) => {
        if (!item.id) {
            return;
        }
        values[lineItemFieldName(item.id, 'name_or_description')] = item.name_or_description ?? '';
        values[lineItemFieldName(item.id, 'net_amount')] = (item.net_amount as Money | null) ?? null;
        values[lineItemFieldName(item.id, 'vat_amount')] = (item.vat_amount as Money | null) ?? null;
        values[lineItemFieldName(item.id, 'vat_id')] = item.vat_id ?? null;
        values[lineItemFieldName(item.id, 'category_id')] = item.category_id ?? undefined;
        values[lineItemFieldName(item.id, 'subledger_id')] = item.subledger_id ?? null;
        values[lineItemFieldName(item.id, 'vat_rate_percentage_id')] = resolveVatRateOptionId(item, vatRateOptions);
        values[lineItemFieldName(item.id, 'transitory_schedule')] =
            item.transitory_start_month && item.transitory_end_month
                ? { start_month: item.transitory_start_month, end_month: item.transitory_end_month }
                : null;

        if (item.net_amount) netAmountsSum += item.net_amount.signed_amount;
        if (item.vat_amount) vatAmountsSum += item.vat_amount.signed_amount;
    });

    return { values, netAmountsSum, vatAmountsSum };
}

function lineItemFromForm(
    id: string,
    form: FormInstance,
    vatRateOptions: DefaultVatRateOption[],
): EditableBillLineItemWire {
    const vatRateOptionId = form.getFieldValue(lineItemFieldName(id, 'vat_rate_percentage_id')) as string | null;
    const selectedOption = vatRateOptions.find(option => option.value === vatRateOptionId);
    const transitorySchedule = form.getFieldValue(lineItemFieldName(id, 'transitory_schedule')) as
        | { start_month: string; end_month: string }
        | null
        | undefined;
    const vatIdRaw = form.getFieldValue(lineItemFieldName(id, 'vat_id')) as string | null | undefined;

    return {
        id,
        name_or_description: (form.getFieldValue(lineItemFieldName(id, 'name_or_description')) as string | undefined) ?? '',
        net_amount: (form.getFieldValue(lineItemFieldName(id, 'net_amount')) as Money | null | undefined) ?? null,
        vat_amount: (form.getFieldValue(lineItemFieldName(id, 'vat_amount')) as Money | null | undefined) ?? null,
        total_amount: null,
        original_fx_net_amount: null,
        original_fx_vat_amount: null,
        vat_rate_percentage: selectedOption?.percentage ?? null,
        vat_id: vatIdRaw && vatIdRaw !== '' ? vatIdRaw : null,
        is_vat_exempt: vatRateOptionId === 'VAT_NONE' ? true : (vatRateOptionId === 'VAT_0' ? false : null),
        category_id: (form.getFieldValue(lineItemFieldName(id, 'category_id')) as string | null | undefined) ?? null,
        transitory_start_month: transitorySchedule?.start_month ?? null,
        transitory_end_month: transitorySchedule?.end_month ?? null,
        subledger_id: (form.getFieldValue(lineItemFieldName(id, 'subledger_id')) as string | null | undefined) ?? null,
        subledger: null,
    };
}

export function buildEditableBillFromForm(
    values: EditorFormValues,
    lineItemIds: string[],
    form: FormInstance,
    vatRateOptions: DefaultVatRateOption[],
    relation: DocumentRelation | null,
): EditableBillWire {
    return {
        document_category: values.document_category ?? null,
        document_direction: values.document_direction ?? null,
        relation_name: values.relation_name || null,
        relation_iban: values.relation_iban || null,
        relation_country: values.relation_country ?? null,
        vat_number: values.vat_number || null,
        reference_identifier: values.reference_identifier || null,
        payment_reference: values.payment_reference || null,
        normalized_date: values.normalized_date || null,
        normalized_time: values.normalized_time || null,
        normalized_due_date: values.normalized_due_date || null,
        total_amount_payable: values.total_amount_payable ?? null,
        vat_amount_overall: values.vat_amount_overall ?? null,
        is_credit_note: !!values.is_credit_note,
        vat_reverse_charge_applied: values.vat_reverse_charge_applied ?? false,
        is_vat_ignored: !values.is_vat_toggled_on,
        relation: relation ? { id: relation.id, name: relation.name } : null,
        line_items: lineItemIds.map((id) => lineItemFromForm(id, form, vatRateOptions)),
        version: values.version,
        content_type: values.content_type,
    };
}
